import React from 'react';
import Meter from './Meter';
import { TYPE } from '../../constants/typeScale';

/**
 * USAGE BAR — a quiet horizontal meter of how much of the current allowance is left, in the
 * Claude.ai idiom: one bar, one plain-language line, and NO per-action pricing.
 *
 * Owner 2026-08-22: "no need to show how much is everything… lets not scare users on usage." So this
 * is the ONE place a creator sees where they stand; individual buttons never quote a price. The bar
 * shows REMAINING out of the plan's cap, so it visibly empties as credits are spent. A purchased
 * top-up can exceed the cap — the bar just clamps to full, which reads as healthy.
 *
 * Presentational: every number arrives as a prop.
 */
export interface UsageBarProps {
  /** Credits still available (WP). */
  remaining: number;
  /** The plan's balance cap — the denominator. */
  cap: number;
  loading?: boolean;
  /** The wallet has never held a credit (a new creator, not one who ran out) — the empty line invites instead. */
  neverFunded?: boolean;
}

/** A new creator's empty wallet (TRY-1): true, and it points at what is free before what is bought. */
export const FIRST_TIME_LINE = 'Your first storyboard is on us. Credits are what you buy to animate and publish.';

/** Never render a 0-width sliver for a non-empty wallet, and never overflow past full. */
const pct = (remaining: number, cap: number) =>
  Math.max(0, Math.min(100, Math.round((remaining / Math.max(1, cap)) * 100)));

const UsageBar: React.FC<UsageBarProps> = ({ remaining, cap, loading, neverFunded }) => {
  const filled = pct(remaining, cap);
  return (
    <div data-testid="usage-bar">
      <Meter percent={filled} label="Credits remaining" loading={loading} testId="usage-bar" />
      <p className={`mt-2 ${TYPE.control} text-secondary-500 dark:text-secondary-400`} data-testid="usage-bar-line">
        {loading
          ? 'Checking your credits…'
          : remaining <= 0 && neverFunded
            ? FIRST_TIME_LINE
          : remaining <= 0
            ? 'You have used all your credits for now — top up to keep creating.'
            : `About ${filled}% of your credits left — ${remaining.toLocaleString()} WP to keep creating with.`}
      </p>
    </div>
  );
};

export default UsageBar;
